import {StrictMode} from 'react';
import {createRoot} from 'react-dom/client';
import App from './App';
import './index.css';

// Global Backend URL Helper
export function getEffectiveBackendUrl(): string {
  try {
    const customUrl = localStorage.getItem("tg_backend_api_url");
    if (customUrl && customUrl.trim()) {
      return customUrl.trim().replace(/\/+$/, "");
    }
  } catch (_) {}

  // Fallback to build-time environment variable if configured
  const envUrl = (import.meta as any).env?.VITE_BACKEND_API_URL;
  if (envUrl && typeof envUrl === "string" && envUrl.trim()) {
    return envUrl.trim().replace(/\/+$/, "");
  }

  return "";
}

// Global Fetch Interceptor to append Authorization Header and route to custom backend URL if set
const originalFetch = window.fetch;

const customFetch = async function (input: any, init: any) {
  const token = localStorage.getItem("auth_token");
  let url = typeof input === "string" ? input : (input as any).url || "";

  // Check if it's a backend API endpoint
  if (url.startsWith("/api/") || url.includes("/api/")) {
    init = init || {};
    const existingHeaders = init.headers || (typeof input !== "string" && (input as any).headers ? (input as any).headers : {});
    const headers = new Headers(existingHeaders);
    if (token && !headers.has("Authorization")) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    init.headers = headers;

    // Prepend custom backend URL if configured (e.g. Cloudflare Pages connecting to remote Cloud Run / VPS)
    const backendUrl = getEffectiveBackendUrl();
    if (backendUrl && url.startsWith("/api/")) {
      url = `${backendUrl}${url}`;
      input = url;
    }
  }

  return originalFetch(input, init);
};

// Try multiple strategies to override window.fetch to avoid read-only or getter-only errors in sandboxed iframes
try {
  // Strategy 1: Object.defineProperty on window
  Object.defineProperty(window, 'fetch', {
    configurable: true,
    enumerable: true,
    writable: true,
    value: customFetch
  });
} catch (e1) {
  console.warn("Strategy 1 (defineProperty on window) failed, trying Strategy 2...", e1);
  try {
    // Strategy 2: Direct assignment
    (window as any).fetch = customFetch;
  } catch (e2) {
    console.warn("Strategy 2 (direct assignment on window) failed, trying Strategy 3...", e2);
    try {
      // Strategy 3: Object.defineProperty on Window.prototype
      Object.defineProperty(Window.prototype, 'fetch', {
        configurable: true,
        enumerable: true,
        writable: true,
        value: customFetch
      });
    } catch (e3) {
      console.error("Strategy 3 (defineProperty on Window.prototype) failed. Fetch interceptor could not be mounted.", e3);
    }
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
